import createDatabase from '@openphone/dexie-database'
import { trackCustomAction } from '@openphone/quo-observability'
import Dexie from 'dexie'
import zip from 'lodash/fp/zip'

import { logError } from '@src/lib/log'
import type MainWorker from '@src/service/worker/main'
import type Repository from '@src/service/worker/repository/base'

import AsyncStorage from './AsyncStorage'
import SyncStorage from './SyncStorage'

const AUTH0_KEY_PREFIX = '@@auth0spajs@@'

export type StorageEngine = AsyncStorage | SyncStorage

const clearCache = async () => {
  try {
    const keys = await window.caches.keys()
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- UXP-3744 - Fix Promise-related ESLint issues
    keys.forEach((key) => window.caches.delete(key))
  } catch (error) {
    // operating on the cache storage is not supported in incognito mode
    // gracefully handle the error since it should not block any further operations
    logError(error)
  }
}
export default class StorageService {
  database: Dexie

  constructor(
    private worker: MainWorker,
    databaseName?: string,
  ) {
    this.database = createDatabase(databaseName)
  }

  async(classConstructor?: (data: any) => any): AsyncStorage {
    return new AsyncStorage(this.database.table('kv'), classConstructor)
  }

  sync<T = unknown>(): SyncStorage<T> {
    return new SyncStorage<T>()
  }

  async reset() {
    this.resetLocalStorage()

    if ('caches' in window) {
      await clearCache()
    }

    if (this.database.isOpen()) {
      const restorableKvKeys = ['FlagsService.flags']

      let restorableKvValues: unknown[] = []

      if (restorableKvKeys.length !== 0) {
        restorableKvValues = await Promise.all(
          restorableKvKeys.map((key) => this.database.table('kv').get(key)),
        )
      }

      await Promise.all(this.database.tables.map((t) => t.clear()))

      if (restorableKvValues.length !== 0) {
        await Promise.all(
          zip(restorableKvKeys, restorableKvValues).map(([key, value]) => {
            if (key && value) {
              return this.database.table('kv').add(value, key)
            }
            return Promise.resolve()
          }),
        )
      }
    }
  }

  async clearAll() {
    this.resetLocalStorage(false)

    if ('caches' in window) {
      await clearCache()
    }

    if (this.database.isOpen()) {
      return this.database.delete().then(() => (this.database = createDatabase()))
    }
  }

  async resetDatabaseIfNecessary() {
    if (this.database.isOpen()) {
      const tableNames = this.database.tables
        .filter((table) => table.name !== 'kv')
        .map((table) => table.name)

      for (const tableName of tableNames) {
        const table = this.database.table(tableName)
        const count = await table.count()

        if (count > 0) {
          await table.clear()
        }
      }
    }
  }

  resetLocalStorage(persistAuthKeys = true) {
    const restorableKeys = new Set([
      'ApiClient.visitorId',
      'ApiClient.visitorIdUpdatedAt',
      /**
       * Used to track the device id.
       * Set by our config
       */
      'deviceId',
      'env-override',
      'backend-preview-override',
      /**
       * When a user joins a workspace during onboarding, we clear the storage
       * and do a page reload so this ensures the user ends up in the correct
       * flow after the page reloads.
       */
      'OnboardingUiState.isJoinWorkspace',
      'AppStore.themeKey',
      'AppStore.themeVariant',
      'VoiceStore.defaultAudioInputDeviceId',
      'VoiceStore.defaultAudioOutputDeviceId',
      'VoiceStore.defaultAudioRingtoneDeviceId',
      /**
       * Identifiers used for tracking user sessions by LaunchDarkly
       */
      'dd_id',
      'dd_anonymous_id',
      'dd_user_id',
      /**
       * Used to display the logout reason
       */
      'UniversalLoginAuthStore.logoutReason',
    ])

    if (persistAuthKeys) {
      /**
       * Used to track the session start time
       */
      restorableKeys.add('UniversalLoginAuthStore.sessionStartedOn')
      /**
       * Used by the auth0-spa-js library to handle race conditions
       */
      restorableKeys.add('auth0.lock.getTokenSilently')
    }

    for (const key of Object.keys(localStorage)) {
      if (
        restorableKeys.has(key) ||
        (persistAuthKeys && key.startsWith(AUTH0_KEY_PREFIX))
      ) {
        continue
      }
      if (
        key.startsWith(AUTH0_KEY_PREFIX) &&
        localStorage.getItem(key)?.includes('"refresh_token"')
      ) {
        trackCustomAction({
          type: 'universal-login-refresh-token-cache-change',
          change: 'cleared-by-reset',
          key,
        })
      }
      localStorage.removeItem(key)
    }
  }

  exists(dbName: string): Promise<boolean> {
    return Dexie.exists(dbName)
  }

  delete(dbName: string): Promise<void> {
    return Dexie.delete(dbName)
  }

  table<T extends Repository>(collection: string): T {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- FIXME: Fix this ESLint violation!
    return this.worker.repo[collection]
  }
}
